<script setup lang="ts">
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import Button from 'primevue/button';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useEditBulkInventoryStore } from '../stores/editBulkInventoryStore';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed } from 'vue';
import { useConfirm } from 'primevue';
import { Assert } from 'shared/assert';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const editBulkInventoryStore = useEditBulkInventoryStore();
const confirm = useConfirm();


const {
  visible,
  problems,
  selectedProductId,
  currentValue,
  newValue,
  saving,
  productOptions,
  bulkStorage,
  storageAreaName,
  selectedProductName,
  selectedProductUnit
} = storeToRefs(editBulkInventoryStore);


const saveBtnWarning = computed<boolean>(() => {
  if(!bulkStorage.value || !selectedProductId.value || bulkStorage.value.content.length === 0) return false;

  if (bulkStorage.value.allowedProducts.length === 0 && bulkStorage.value.allowMultipleProducts === false) {
    if (bulkStorage.value.content.some(p => p.productId === selectedProductId.value)) {
      return false;
    } else {
      return true;
    }
  }
return false;
});

function onEmptyClick() {
  newValue.value = 0;
  editBulkInventoryStore.save();
}

function onSaveClick() {
  if (saveBtnWarning.value) {
    Assert.definied(storageAreaName.value);
    Assert.definied(selectedProductName.value);

    confirm.require({
      header: translations.value.dialogs.inventory.confirmHeader(storageAreaName.value),
      message: translations.value.dialogs.inventory.confirmMessage(storageAreaName.value, selectedProductName.value),
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: translations.value.generic.abort,
      rejectProps: {
        label: translations.value.generic.abort,
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: translations.value.generic.confirm,
        severity: 'warn',
      },
      accept: () => {
        editBulkInventoryStore.save();
      },
    });
  } else {
    editBulkInventoryStore.save();
  }
}


</script>

<template>
  <Dialog v-model:visible="visible" :header="translations.dialogs.inventory.title + ' ' + storageAreaName" :modal="false" :draggable="true"
    :closable="true" id="bulk-edit-inventory-dialog">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>

    <div class="field">
      <label for="product-select"> {{ translations.dialogs.inventory.selectProduct }} </label>
      <Select id="bulk-edit-inventory-current-count-input" v-model="selectedProductId"
        :options="productOptions" option-label="name" option-value="id">
      </Select>
    </div>

    <div v-if="selectedProductId" class="current-amount">
      <label for="bulk-edit-inventory-current-count-input">
        {{ translations.dialogs.inventory.currentAmount }}
      </label>
      <span>{{ currentValue }}</span>
    </div>

    <div v-if="selectedProductId" class="field">
      <label for="bulk-edit-inventory-new-count-input">
        {{ translations.dialogs.inventory.setNewAmount }}
      </label>
      <InputGroup>
      <InputNumber pt:pcInputText:root:autofocus v-model:model-value="newValue"
        id="bulk-edit-inventory-new-count-input" class="number-field"></InputNumber>
        <InputGroupAddon>{{ translations.bulk.bulkUnitShort(selectedProductUnit) }}</InputGroupAddon>
      </InputGroup>
    </div>

    <div class="actions">
      <Button v-if="selectedProductId" :label="translations.generic.empty" :loading="saving" @click="onEmptyClick"></Button>
      <Button icon="pi pi-save" :label="translations.generic.save" :severity="saveBtnWarning ? 'warn' : 'primary' " :loading="saving"
        v-if="selectedProductId" @click="onSaveClick"></Button>
    </div>
  </Dialog>
</template>

<style scoped>
.field {
  display: grid;
  grid-template-rows: repeat(2, auto);
}

.actions {
  display: flex;
  justify-content: end;
  column-gap: .5rem;
}

.current-amount {
  display: grid;
  column-gap: .5rem;
  grid-template-columns: auto 1fr;
}

.current-amount span {
  text-align: start;
}

:deep(.p-inputnumber-input) {
  text-align: end;
}
</style>

<style>
#bulk-edit-inventory-dialog {
  min-width: 20rem;
}

#bulk-edit-inventory-dialog .p-dialog-content {
  display: flex;
  grid-template-columns: 1fr;
  flex-direction: column;
  gap: 1rem;
}
</style>
